import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { adminService } from '../services/api';
import {
  BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import '../styles/AdminDashboard.css';

const AdminDashboard = () => {
  const { esAdmin } = useAuth();
  const navigate = useNavigate();
  const [metricas, setMetricas] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!esAdmin) {
      navigate('/');
      return;
    }

    cargarMetricas();
  }, [esAdmin, navigate]);

  const cargarMetricas = async () => {
    try {
      setLoading(true);
      const response = await adminService.getMetricas();
      setMetricas(response.data);
      setError('');
    } catch (err) {
      console.error('Error al cargar métricas:', err);
      setError('Error al cargar las métricas del sistema');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="admin-dashboard">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Cargando métricas del sistema...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-dashboard">
        <div className="error-container">
          <p>{error}</p>
          <button onClick={cargarMetricas} className="btn-retry">
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  if (!metricas || !metricas.proyectos || !metricas.cotizaciones) return null;

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d'];

  // Preparar datos para gráficos - mostrar todos los estados (incluso con valor 0)
  const datosProyectosPorEstado = metricas.proyectos?.por_estado ? [
    { name: 'Planificación', value: metricas.proyectos.por_estado.planificacion || 0 },
    { name: 'En Curso', value: metricas.proyectos.por_estado.en_curso || 0 },
    { name: 'Pausado', value: metricas.proyectos.por_estado.pausado || 0 },
    { name: 'Completado', value: metricas.proyectos.por_estado.completado || 0 },
    { name: 'Cancelado', value: metricas.proyectos.por_estado.cancelado || 0 }
  ] : [];

  const datosCotizacionesPorEstado = metricas.cotizaciones?.por_estado ? [
    { name: 'Borrador', value: metricas.cotizaciones.por_estado.borrador || 0 },
    { name: 'Enviada', value: metricas.cotizaciones.por_estado.enviada || 0 },
    { name: 'Aprobada', value: metricas.cotizaciones.por_estado.aprobada || 0 },
    { name: 'Rechazada', value: metricas.cotizaciones.por_estado.rechazada || 0 }
  ] : [];

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: 'CLP'
    }).format(value);
  };

  return (
    <div className="admin-dashboard">
      <div className="dashboard-header">
        <h1>Panel de Administración</h1>
        <p className="subtitle">Vista general del sistema AppContra</p>
        <button onClick={cargarMetricas} className="btn-refresh">
          Actualizar Datos
        </button>
      </div>

      {/* Métricas Generales */}
      <div className="metrics-grid">
        {/* Usuarios */}
        <div className="metric-card usuarios-card">
          <div className="metric-header">
            <h3>Usuarios</h3>
            <span className="metric-icon">👥</span>
          </div>
          <div className="metric-body">
            <div className="metric-main">
              <span className="metric-value">{metricas.usuarios?.total_usuarios || 0}</span>
              <span className="metric-label">Total Usuarios</span>
            </div>
            <div className="metric-details">
              <div className="metric-item">
                <span className="label">Contratistas:</span>
                <span className="value">{metricas.usuarios?.total_contratistas || 0}</span>
              </div>
              <div className="metric-item">
                <span className="label">Capataces:</span>
                <span className="value">{metricas.usuarios?.total_capataces || 0}</span>
              </div>
              <div className="metric-item">
                <span className="label">Admins:</span>
                <span className="value">{metricas.usuarios?.total_admins || 0}</span>
              </div>
              <div className="metric-item highlight">
                <span className="label">Nuevos (30 días):</span>
                <span className="value">{metricas.usuarios?.nuevos_ultimos_30_dias || 0}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Proyectos */}
        <div className="metric-card proyectos-card">
          <div className="metric-header">
            <h3>Proyectos</h3>
            <span className="metric-icon">🏗️</span>
          </div>
          <div className="metric-body">
            <div className="metric-main">
              <span className="metric-value">{metricas.proyectos?.total_proyectos || 0}</span>
              <span className="metric-label">Total Proyectos</span>
            </div>
            <div className="metric-details">
              <div className="metric-item">
                <span className="label">Planificación:</span>
                <span className="value">{metricas.proyectos?.por_estado?.planificacion || 0}</span>
              </div>
              <div className="metric-item active">
                <span className="label">En Curso:</span>
                <span className="value">{metricas.proyectos?.por_estado?.en_curso || 0}</span>
              </div>
              <div className="metric-item">
                <span className="label">Pausados:</span>
                <span className="value">{metricas.proyectos?.por_estado?.pausado || 0}</span>
              </div>
              <div className="metric-item success">
                <span className="label">Completados:</span>
                <span className="value">{metricas.proyectos?.por_estado?.completado || 0}</span>
              </div>
              <div className="metric-item">
                <span className="label">Cancelados:</span>
                <span className="value">{metricas.proyectos?.por_estado?.cancelado || 0}</span>
              </div>
              <div className="metric-item highlight">
                <span className="label">Presupuesto Total:</span>
                <span className="value">{formatCurrency(metricas.proyectos?.presupuesto_total || 0)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Cotizaciones */}
        <div className="metric-card cotizaciones-card">
          <div className="metric-header">
            <h3>Cotizaciones</h3>
            <span className="metric-icon">📋</span>
          </div>
          <div className="metric-body">
            <div className="metric-main">
              <span className="metric-value">{metricas.cotizaciones?.total_cotizaciones || 0}</span>
              <span className="metric-label">Total Cotizaciones</span>
            </div>
            <div className="metric-details">
              <div className="metric-item">
                <span className="label">Borradores:</span>
                <span className="value">{metricas.cotizaciones?.por_estado?.borrador || 0}</span>
              </div>
              <div className="metric-item">
                <span className="label">Enviadas:</span>
                <span className="value">{metricas.cotizaciones?.por_estado?.enviada || 0}</span>
              </div>
              <div className="metric-item success">
                <span className="label">Aprobadas:</span>
                <span className="value">{metricas.cotizaciones?.por_estado?.aprobada || 0}</span>
              </div>
              <div className="metric-item">
                <span className="label">Rechazadas:</span>
                <span className="value">{metricas.cotizaciones?.por_estado?.rechazada || 0}</span>
              </div>
              <div className="metric-item highlight">
                <span className="label">Valor Total:</span>
                <span className="value">{formatCurrency(metricas.cotizaciones?.valor_total || 0)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Trabajadores */}
        <div className="metric-card trabajadores-card">
          <div className="metric-header">
            <h3>Trabajadores</h3>
            <span className="metric-icon">👷</span>
          </div>
          <div className="metric-body">
            <div className="metric-main">
              <span className="metric-value">{metricas.trabajadores?.total_trabajadores || 0}</span>
              <span className="metric-label">Total Trabajadores</span>
            </div>
            <div className="metric-details">
              <div className="metric-item active">
                <span className="label">Activos:</span>
                <span className="value">{metricas.trabajadores?.activos || 0}</span>
              </div>
              <div className="metric-item">
                <span className="label">Inactivos:</span>
                <span className="value">{metricas.trabajadores?.inactivos || 0}</span>
              </div>
              <div className="metric-item highlight">
                <span className="label">Nuevos (30 días):</span>
                <span className="value">{metricas.trabajadores?.nuevos_ultimos_30_dias || 0}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Gastos */}
        <div className="metric-card gastos-card">
          <div className="metric-header">
            <h3>Gastos</h3>
            <span className="metric-icon">💰</span>
          </div>
          <div className="metric-body">
            <div className="metric-main">
              <span className="metric-value">{metricas.gastos?.total_gastos || 0}</span>
              <span className="metric-label">Total Gastos</span>
            </div>
            <div className="metric-details">
              <div className="metric-item highlight">
                <span className="label">Monto Total:</span>
                <span className="value">{formatCurrency(metricas.gastos?.monto_total || 0)}</span>
              </div>
              <div className="metric-item">
                <span className="label">Últimos 30 días:</span>
                <span className="value">{formatCurrency(metricas.gastos?.monto_ultimos_30_dias || 0)}</span>
              </div>
              <div className="metric-item">
                <span className="label">Promedio/Gasto:</span>
                <span className="value">{formatCurrency(metricas.gastos?.promedio_por_gasto || 0)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Clientes */}
        <div className="metric-card clientes-card">
          <div className="metric-header">
            <h3>Clientes</h3>
            <span className="metric-icon">🏢</span>
          </div>
          <div className="metric-body">
            <div className="metric-main">
              <span className="metric-value">{metricas.clientes?.total_clientes || 0}</span>
              <span className="metric-label">Total Clientes</span>
            </div>
            <div className="metric-details">
              <div className="metric-item active">
                <span className="label">Activos:</span>
                <span className="value">{metricas.clientes?.activos || 0}</span>
              </div>
              <div className="metric-item">
                <span className="label">Inactivos:</span>
                <span className="value">{metricas.clientes?.inactivos || 0}</span>
              </div>
              <div className="metric-item highlight">
                <span className="label">Nuevos (30 días):</span>
                <span className="value">{metricas.clientes?.nuevos_ultimos_30_dias || 0}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Gráficos */}
      <div className="charts-section">
        <div className="chart-container">
          <h3>Distribución de Proyectos por Estado</h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={datosProyectosPorEstado}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                outerRadius={100}
                fill="#8884d8"
                dataKey="value"
              >
                {datosProyectosPorEstado.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>

        <div className="chart-container">
          <h3>Distribución de Cotizaciones por Estado</h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={datosCotizacionesPorEstado}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                outerRadius={100}
                fill="#82ca9d"
                dataKey="value"
              >
                {datosCotizacionesPorEstado.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>

        <div className="chart-container full-width">
          <h3>Proyectos Creados por Mes (Últimos 12 meses)</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={metricas.proyectos?.proyectos_por_mes || []}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="mes" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="cantidad" fill="#0088FE" name="Proyectos" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Top Contratistas */}
      {metricas.proyectos?.proyectos_por_contratista && metricas.proyectos.proyectos_por_contratista.length > 0 && (
        <div className="top-contractors-section">
          <h3>Top Contratistas por Proyectos</h3>
          <div className="contractors-table">
            <table>
              <thead>
                <tr>
                  <th>Posición</th>
                  <th>Contratista</th>
                  <th>Empresa</th>
                  <th>Total Proyectos</th>
                  <th>En Curso</th>
                  <th>Completados</th>
                </tr>
              </thead>
              <tbody>
                {metricas.proyectos.proyectos_por_contratista.slice(0, 10).map((contratista, index) => (
                  <tr key={contratista.contratista_id}>
                    <td className="position">
                      {index + 1}
                      {index === 0 && <span className="medal gold">🥇</span>}
                      {index === 1 && <span className="medal silver">🥈</span>}
                      {index === 2 && <span className="medal bronze">🥉</span>}
                    </td>
                    <td>{contratista.nombre}</td>
                    <td>{contratista.nombre_empresa || '-'}</td>
                    <td className="highlight">{contratista.total_proyectos}</td>
                    <td>{contratista.proyectos_en_curso}</td>
                    <td>{contratista.proyectos_completados}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Actividad Reciente */}
      {metricas.actividad_reciente && metricas.actividad_reciente.length > 0 && (
        <div className="recent-activity-section">
          <h3>Actividad Reciente (Últimos 7 días)</h3>
          <div className="activity-timeline">
            {metricas.actividad_reciente.map((actividad, index) => (
              <div key={index} className="activity-item">
                <div className="activity-icon">
                  {actividad.tipo === 'proyecto' && '🏗️'}
                  {actividad.tipo === 'cotizacion' && '📋'}
                  {actividad.tipo === 'usuario' && '👤'}
                </div>
                <div className="activity-details">
                  <div className="activity-title">{actividad.titulo}</div>
                  <div className="activity-date">
                    {new Date(actividad.fecha).toLocaleDateString('es-CL', {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
